import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { NextButton } from '@/components/buttons/Buttons';
import Container, { FlexSpaceBetweenContainer } from '@/components/container/Container';
import Spacing from '@/components/container/Spacing';
import CheckboxInput from '@/components/form-inputs/CheckboxInput';
import SelectCountryDropdown from '@/components/form-inputs/select-country/SelectCountryDropdown';
import { TextHeadlineLarge } from '@/components/text/Texts';
import { NEXT } from '@/constants/button-labels';
import { SIGNUP_FORM } from '@/constants/forms';
import IdGeneratorService from '@/services/id-generator.service';
import type { FormProps } from '@/types';
import { type SignupFormData, signupInitialValues, signupSchema } from '../../engine/sign-up.model';
import TermsOfUseCheckboxLabel from './TermsOfUseCheckboxLabel';
import { PhoneControl } from './PhoneControl';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { checkEmailAvailability } from '../../engine/authentication.mutations';
import { showError } from '@/modules/notification/engine/notification.library';
import { useSignupStore } from '@/store';
import TextInput from '@/components/inputs/TextInput';
import { InputController } from '@/components/inputs/InputController';

const SignUpForm: React.FC<FormProps<SignupFormData>> = ({ onSubmit }) => {
	// Get pre-filled data from signup store (e.g., from Google OAuth)
	const signupStoreData = useSignupStore((state) => state.user);

	// Check if email came from OAuth provider (Google/Facebook)
	const isEmailFromOAuth = Boolean(signupStoreData.oauthProvider && signupStoreData.email);

	// Merge store data with initial values
	const defaultValues: SignupFormData = {
		firstName: signupStoreData.firstName || signupInitialValues.firstName,
		lastName: signupStoreData.lastName || signupInitialValues.lastName,
		mobileNumberPrefix: signupStoreData.mobileNumberPrefix || signupInitialValues.mobileNumberPrefix,
		mobileNumber: signupStoreData.mobileNumber || signupInitialValues.mobileNumber,
		email: signupStoreData.email || signupInitialValues.email,
		agree: signupInitialValues.agree,
	};

	const form = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(signupSchema),
		mode: 'all',
	});

	const emailCheckMutation = useMutation({
		mutationFn: checkEmailAvailability,
		onSuccess: (response) => {
			form.setError('email', { message: response.data.IsAvailable ? '' : 'This email is already registered' });
		},
		onError: (error) => {
			if (!isAxiosError(error)) return;
			showError(error.message ?? '', { summary: 'Error' });
		}
	});

	const handleFormSubmit = (data: SignupFormData) => {
		emailCheckMutation.mutate(data.email, {
			onSuccess: (response) => response.data.IsAvailable && onSubmit(data)
		});
	};

	const isButtonDisabled =
		!form.formState.isValid
		|| Boolean(form.formState.errors.email)
		|| emailCheckMutation.isPending;

	return (
		<form
			id={new IdGeneratorService(SIGNUP_FORM).formId}
			onSubmit={form.handleSubmit(handleFormSubmit)}
			noValidate
		>
			<Container className="flex flex-col p-0 m-0">
				<TextHeadlineLarge text="Sign up" className="text-inverseSurface" />
				<Container className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 pb-4 pt-8">
					<InputController id="firstName" control={form.control}>
						<TextInput label="First Name" />
					</InputController>
					<InputController id="lastName" control={form.control}>
						<TextInput label="Last Name" />
					</InputController>

					<Controller
						name="mobileNumberPrefix"
						control={form.control}
						defaultValue='+63'
						render={({ field, fieldState: { error } }) => (
							<SelectCountryDropdown
								value={field.value}
								onChange={field.onChange}
							/>
						)}
					/>
					{/* <SelectCountryDropdown onChange={(code) => setCountryCode(code)} /> */}

					<PhoneControl
						// label="Mobile No."
						control={form.control}
						// formName={SIGNUP_FORM}
						id="mobileNumber"
						countryCode='PH'
					/>
				</Container>

				<InputController id="email" control={form.control}>
					<TextInput label="Email" disabled={isEmailFromOAuth} />
				</InputController>

				<Container className="py-3 mt-4">
					<CheckboxInput
						control={form.control}
						formName={SIGNUP_FORM}
						id="agree"
					>
						<TermsOfUseCheckboxLabel />
					</CheckboxInput>
				</Container>
				<Spacing height="24px" />
			</Container>

			<Container className="pt-[24px]">
				<FlexSpaceBetweenContainer>
					{/* <BackButton id="sign-up-back-button" onClick={onBack} /> */}
					<NextButton
						id="sign-up-next-button"
						className={`w-full ${!form.formState.isDirty || isButtonDisabled ? 'disabled-button' : 'enabled-button'}`}
						disabled={isButtonDisabled}
						label={NEXT}
						type="submit"
						withIcon={false}
					/>
				</FlexSpaceBetweenContainer>
			</Container>
		</form>
	);
};

export default SignUpForm;
